import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from '@/components/icons';
import { useMutation } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/form/Input';
import { useToast } from '@/components/ui/useToast';
import { apiError } from '@/lib/apiError';
import { bulkCreateLocations, type Location, type LocationLevel } from '@/api/locations';

interface Props {
  open: boolean;
  parent: Location | null;
  level: LocationLevel;
  onClose: () => void;
  onDone: () => void;
}

type Mode = 'range' | 'paste';

/**
 * Range pattern language:
 *   Any literal characters, plus a single `{start..end}` (numeric) or
 *   `{start..end:pad}` (zero-padded). `{parent}` expands to parent code.
 *   Examples:
 *     "B-{parent}-{01..12}"  →  B-A1-01-01, B-A1-01-02, ..., B-A1-01-12
 *     "{parent}-{1..5}"      →  A1-1, A1-2, ..., A1-5
 */
function expandPattern(pattern: string, parentCode: string): { codes: string[]; error: string | null } {
  const p = pattern.replace(/\{parent\}/g, parentCode);
  const m = p.match(/\{(-?\d+)\.\.(-?\d+)(?::(\d+))?\}/);
  if (!m) {
    return p ? { codes: [p], error: null } : { codes: [], error: 'Pattern is empty' };
  }
  const start = Number(m[1]);
  const end = Number(m[2]);
  const pad = m[3] ? Number(m[3]) : 0;
  if (Number.isNaN(start) || Number.isNaN(end)) return { codes: [], error: 'Invalid range numbers' };
  if (end < start) return { codes: [], error: 'Range end must be >= start' };
  if (end - start > 499) return { codes: [], error: 'Range too large (max 500)' };
  // Detect implicit padding from the raw literal, e.g. "{01..12}"
  const implicitPad = m[1].startsWith('0') || m[1].startsWith('-0') ? m[1].replace('-', '').length : 0;
  const effPad = Math.max(pad, implicitPad);
  const codes: string[] = [];
  for (let i = start; i <= end; i++) {
    const num = effPad > 0 ? String(Math.abs(i)).padStart(effPad, '0') : String(Math.abs(i));
    const sign = i < 0 ? '-' : '';
    codes.push(p.replace(/\{-?\d+\.\.-?\d+(?::\d+)?\}/, `${sign}${num}`));
  }
  return { codes, error: null };
}

export function BulkAddLocationsModal({ open, parent, level, onClose, onDone }: Props) {
  const toast = useToast();
  const [mode, setMode] = useState<Mode>('range');
  const [pattern, setPattern] = useState('');
  const [pasted, setPasted] = useState('');

  useEffect(() => {
    if (!open) return;
    setPattern('RACK-{01..10}');
    setPasted('');
    setMode('range');
  }, [open, parent, level]);

  const preview = useMemo(() => {
    if (mode === 'range') {
      const { codes, error } = expandPattern(pattern.trim(), parent?.code ?? '');
      return { codes, error };
    }
    const codes = pasted
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (codes.length === 0) return { codes: [], error: 'Paste at least one code, one per line' };
    if (codes.length > 500) return { codes: codes.slice(0, 500), error: 'Truncated to 500 codes' };
    return { codes, error: null };
  }, [mode, pattern, pasted, parent?.code]);

  const dupInList = useMemo(() => {
    const seen = new Set<string>();
    const dupes = new Set<string>();
    preview.codes.forEach((c) => {
      if (seen.has(c)) dupes.add(c);
      seen.add(c);
    });
    return dupes;
  }, [preview.codes]);

  const mut = useMutation({
    mutationFn: bulkCreateLocations,
    onSuccess: (res) => {
      onDone();
      if (res.created > 0) toast.push(`Created ${res.created} location(s)${res.skipped ? `; skipped ${res.skipped}` : ''}`, 'success');
      else toast.push(`Nothing created — all ${res.skipped} codes already exist`, 'info');
      if (res.created > 0) onClose();
    },
    onError: (e: unknown) => {
      toast.push(apiError(e, 'Bulk create failed'), 'error');
    },
  });

  function submit() {
    if (preview.codes.length === 0 || dupInList.size > 0) return;
    mut.mutate({
      parentId: parent?.id ?? null,
      level,
      codes: preview.codes,
    });
  }

  const previewList = preview.codes.slice(0, 50);
  const overflow = Math.max(0, preview.codes.length - previewList.length);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Bulk Add Locations"
      size="md"
      footer={
        <>
          <Button variant="glass" onClick={onClose}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={
              mut.isPending ||
              !!preview.error ||
              preview.codes.length === 0 ||
              dupInList.size > 0
            }
          >
            Create {preview.codes.length} location{preview.codes.length === 1 ? '' : 's'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {/* Mode toggle */}
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => setMode('range')}
            className="rounded-lg px-3 py-2 text-xs font-bold border transition"
            style={{
              background: mode === 'range' ? 'var(--accent)' : 'var(--bg-card)',
              color: mode === 'range' ? '#000' : 'var(--text-secondary)',
              borderColor: mode === 'range' ? 'var(--accent)' : 'var(--border-color)',
            }}
          >
            Range Pattern
          </button>
          <button
            onClick={() => setMode('paste')}
            className="rounded-lg px-3 py-2 text-xs font-bold border transition"
            style={{
              background: mode === 'paste' ? 'var(--accent)' : 'var(--bg-card)',
              color: mode === 'paste' ? '#000' : 'var(--text-secondary)',
              borderColor: mode === 'paste' ? 'var(--accent)' : 'var(--border-color)',
            }}
          >
            Paste List
          </button>
        </div>

        {/* Inputs */}
        {mode === 'range' ? (
          <div>
            <label className="mb-1 block text-[11px] font-bold text-[var(--text-secondary)]">
              Pattern
            </label>
            <Input
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
              placeholder="e.g. RACK-{01..12}"
              autoFocus
            />
            <div className="mt-1.5 text-[11px] leading-relaxed text-[var(--text-muted)]">
              Use <code className="rounded bg-[var(--bg-soft)] px-1 py-0.5 text-[10px]">{'{start..end}'}</code>
              &nbsp;for a numeric range. Zero-pad by starting with 0, e.g. <code>{'{01..12}'}</code>.
            </div>
          </div>
        ) : (
          <div>
            <label className="mb-1 block text-[11px] font-bold text-[var(--text-secondary)]">
              Codes (one per line, up to 500)
            </label>
            <textarea
              value={pasted}
              onChange={(e) => setPasted(e.target.value)}
              rows={8}
              className="w-full rounded-lg border border-[var(--border-color)] bg-[var(--input-bg)] px-3 py-2 text-sm font-mono text-[var(--text-primary)] outline-none focus:border-accent"
              placeholder={`RACK-001\nRACK-002\nSHELF-A5`}
            />
          </div>
        )}

        {/* Preview */}
        <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-card)] p-3">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-[11px] font-extrabold uppercase tracking-widest text-[var(--text-muted)]">
              Preview
            </div>
            <div className="text-[11px] font-bold text-[var(--text-secondary)]">
              {preview.codes.length} code{preview.codes.length === 1 ? '' : 's'}
              {dupInList.size > 0 && (
                <span className="ml-2 text-danger">{dupInList.size} duplicate{dupInList.size === 1 ? '' : 's'} in list</span>
              )}
            </div>
          </div>
          {preview.error && (
            <div className="mb-2 rounded border border-danger/30 bg-danger/10 px-2 py-1.5 text-[11px] text-danger">
              {preview.error}
            </div>
          )}
          {preview.codes.length === 0 ? (
            <div className="rounded border border-dashed border-[var(--border-color)] py-4 text-center text-xs text-[var(--text-muted)]">
              No codes yet.
            </div>
          ) : (
            <div className="max-h-40 overflow-y-auto">
              <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 font-mono text-[11px]">
                {previewList.map((c, i) => {
                  const dup = dupInList.has(c);
                  return (
                    <div
                      key={i}
                      className={
                        'truncate ' +
                        (dup ? 'text-danger font-bold' : 'text-[var(--text-primary)]')
                      }
                    >
                      {c}{dup && <AlertTriangle size={11} className="ml-1 inline-block align-[-1px] text-warning" />}
                    </div>
                  );
                })}
              </div>
              {overflow > 0 && (
                <div className="mt-1 text-[11px] italic text-[var(--text-muted)]">
                  …and {overflow} more
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
